import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import styled from 'styled-components'
import { ExpandMore, ChevronRight } from '@material-ui/icons'

import { strings } from '@constants'
import { Spacings, Text } from '@styles'
import { uploadInterestPaymentDocuments } from '@data'
import { updateOrganisationApi } from '@data/conn/put/organisation'
import { showLoader, showToast } from '@data/state/action'
import { toggleDrawer } from '@data/state/action/root'
import { DocumentDownload, DrawerBodyWrapper, DrawerFooterWrapper, RadioButton, TOAST_TYPE, UploadMultipleFileButton } from '@base'
import { Button, Calendar, Input } from '@baseV2'

import { InputsWrapper } from '../styles'

const Row = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: ${Spacings.SPACING_3B};
`

const RadioRow = styled.div`
	display: flex;
	align-items: center;
	gap: ${Spacings.SPACING_4B};
	flex-wrap: wrap;
`

const RadioGroup = styled.div`
	display: flex;
	align-items: center;
	gap: ${Spacings.SPACING_6B};
`

const RadioOption = styled.div`
	display: flex;
	align-items: center;
	gap: ${Spacings.SPACING_2B};
`

const RadioLabel = styled.span`
	font-size: ${Text.MEDIUM};
	color: ${({ theme }) => theme.colors.text.primary};
`

const FieldTitle = styled.span`
	font-size: ${Text.SMALL};
	font-weight: 600;
	color: ${({ theme }) => theme.colors.text.primary};
`

const DocCard = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${Spacings.SPACING_3B};
	padding: ${Spacings.SPACING_4B};
	border: 1px solid ${({ theme }) => theme.colors.border.default};
	border-radius: ${({ theme }) => theme.radius.md};
`

const DocHeader = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: ${Spacings.SPACING_2B};
`

const HeaderRight = styled.div`
	display: flex;
	align-items: center;
	gap: ${Spacings.SPACING_2B};
`

const ChevronBtn = styled.button`
	display: flex;
	align-items: center;
	justify-content: center;
	background: none;
	border: none;
	cursor: pointer;
	color: ${({ theme }) => theme.colors.text.secondary};
`

const DocTilesRow = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: ${Spacings.SPACING_3B};
`

const EmptyState = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${Spacings.SPACING_1B};
	font-size: ${Text.SMALL};
	color: ${({ theme }) => theme.colors.text.secondary};
`

const FooterActions = styled.div`
	display: flex;
	width: 100%;
	gap: ${Spacings.SPACING_3B};
	justify-content: flex-end;
`

const deriveFormState = (orgDetails) => ({
	interest: orgDetails?.interest_payment?.interest ?? '',
	interest_received: Boolean(orgDetails?.interest_payment?.interest_received),
	documents: orgDetails?.interest_payment?.documents ?? [],
	interest_payment_date: orgDetails?.interest_payment?.interest_payment_date ?? null,
})

const InterestPayment = ({ orgDetails, update }) => {
	const dispatch = useDispatch()
	const [formState, setFormState] = useState(deriveFormState(orgDetails))
	const [isOpen, setIsOpen] = useState(Boolean(orgDetails?.interest_payment?.interest_payment_date))

	useEffect(() => {
		setFormState(deriveFormState(orgDetails))
		setIsOpen(Boolean(orgDetails?.interest_payment?.interest_payment_date))
	}, [orgDetails])

	const updateState = (key) => (value) => setFormState((_s) => ({ ..._s, [key]: value }))

	const interestReceivedNoHandler = () =>
		setFormState((_s) => ({ ..._s, interest_received: false, interest: '', interest_payment_date: null, documents: [] }))

	const uploadDocumentsHandler = (files) => {
		if (!files || files.length < 1) return
		dispatch(showLoader(true))
		Promise.all(Array.from(files).map((file) => uploadInterestPaymentDocuments(file, orgDetails?.organisation_id)))
			.then((responses) => {
				setFormState((_s) => ({ ..._s, documents: [..._s.documents, ...responses.map((res) => res?.file_paths)]?.flat() }))
				dispatch(showToast(true, strings('uploaded_the_file_successfully'), { type: TOAST_TYPE.SUCCESS }))
			})
			.catch((err) => {
				console.log(err)
				dispatch(showToast(true, strings('document_upload_failed'), { type: TOAST_TYPE.ERROR }))
			})
			.finally(() => dispatch(showLoader(false)))
	}

	const removeDocumentHandler = (index) =>
		setFormState((_s) => ({ ..._s, documents: _s.documents.filter((_, _index) => _index !== index) }))

	const onInterestUpdate = () => {
		if (formState.interest_received === true && !formState.interest) return
		dispatch(showLoader(true))
		updateOrganisationApi(orgDetails.organisation_id, 'INTEREST_PAYMENT', null, {
			interest: Number(formState.interest),
			interest_received: formState.interest_received,
			documents: formState.documents,
			interest_payment_date: formState.interest_payment_date,
		})
			.then(() => {
				dispatch(showToast(true, strings('organisation_update_success'), { type: TOAST_TYPE.SUCCESS }))
				dispatch(toggleDrawer(false))
				update()
			})
			.catch((err) => {
				console.log(err)
				dispatch(showToast(true, strings('organisation_update_failure'), { type: TOAST_TYPE.ERROR }))
			})
			.finally(() => dispatch(showLoader(false)))
	}

	const disabled = formState.interest_received === false
	const hasDocs = formState.documents.length > 0

	return (
		<>
			<DrawerBodyWrapper>
				<InputsWrapper>
					<RadioRow>
						<FieldTitle>Interest approval recd. from Client :</FieldTitle>
						<RadioGroup>
							<RadioOption>
								<RadioButton
									id='interest-received-yes'
									size='1.125'
									type='bool'
									checked={formState.interest_received === true}
									value={true}
									onChange={() => updateState('interest_received')(true)}
								/>
								<RadioLabel>{strings('yes')}</RadioLabel>
							</RadioOption>
							<RadioOption>
								<RadioButton
									id='interest-received-no'
									size='1.125'
									type='bool'
									checked={formState.interest_received === false}
									value={false}
									onChange={interestReceivedNoHandler}
								/>
								<RadioLabel>{strings('no')}</RadioLabel>
							</RadioOption>
						</RadioGroup>
					</RadioRow>

					<Row>
						<Input
							id='interest-v2'
							type='number'
							label='Interest (%)'
							placeholder={strings('enter', 'interest')}
							value={formState.interest}
							onChange={(e) => updateState('interest')(e.target.value)}
							disabled={disabled}
						/>
						<Calendar
							id='interest-payment-date-v2'
							label='Applicable From'
							placeholder={strings(['select', 'starting', 'date'])}
							value={formState.interest_payment_date}
							onChange={updateState('interest_payment_date')}
							maxDate={new Date()}
							disabled={disabled}
						/>
					</Row>

					<DocCard>
						<DocHeader>
							<FieldTitle>
								{strings('documents')}{' '}
								{hasDocs && `(${formState.documents.length}-${formState.documents.length > 1 ? strings('documents') : strings('document')})`}
							</FieldTitle>
							<HeaderRight>
								<UploadMultipleFileButton
									xs
									type='secondary'
									label={strings('upload', 'documents')}
									onChange={uploadDocumentsHandler}
									disabled={disabled}
								/>
								<ChevronBtn type='button' onClick={() => setIsOpen((v) => !v)}>
									{isOpen ? <ExpandMore /> : <ChevronRight />}
								</ChevronBtn>
							</HeaderRight>
						</DocHeader>
						{isOpen &&
							formState.interest_received === true &&
							(hasDocs ? (
								<DocTilesRow>
									{formState.documents.map((item, index) => (
										<DocumentDownload key={`interest-document-${index}`} link={item} type='interest_payment' onDelete={() => removeDocumentHandler(index)} />
									))}
								</DocTilesRow>
							) : (
								<EmptyState>
									<span>{strings('no_document_has_been_uploaded')}</span>
									<span>{strings('click_upload_to_add_document')}</span>
								</EmptyState>
							))}
					</DocCard>
				</InputsWrapper>
			</DrawerBodyWrapper>
			<DrawerFooterWrapper>
				<FooterActions>
					<Button variant='outline' color='danger' onClick={() => dispatch(toggleDrawer(false))}>
						{strings('cancel')}
					</Button>
					<Button color='primary' disabled={formState.interest_received === true && !formState.interest} onClick={onInterestUpdate}>
						{strings('update')}
					</Button>
				</FooterActions>
			</DrawerFooterWrapper>
		</>
	)
}

export default InterestPayment
